import type { Permission } from '@talentbabu/types';
import {
  BarChart3,
  Bell,
  FileSpreadsheet,
  Bookmark,
  Briefcase,
  Building2,
  CalendarCheck,
  ClipboardCheck,
  ClipboardList,
  ShieldCheck,
  UserSearch,
  CreditCard,
  IndianRupee,
  Gift,
  Globe,
  Layers,
  ReceiptText,
  MailPlus,
  LifeBuoy,
  Newspaper,
  LayoutTemplate,
  MessagesSquare,
  Handshake,
  ListTodo,
  Target,
  TrendingUp,
  FileText,
  Inbox,
  LayoutDashboard,
  ScrollText,
  Search,
  Send,
  Settings,
  SlidersHorizontal,
  UserCircle,
  Users,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Hidden unless the user holds this permission (UI convenience only). */
  permission?: Permission;
  /** Shown in the mobile bottom bar (max 4). */
  mobile?: boolean;
}

export interface NavConfig {
  title: string;
  items: NavItem[];
  theme: 'light' | 'dark';
}

export const CANDIDATE_NAV: NavConfig = {
  title: 'Candidate',
  theme: 'light',
  items: [
    { to: '/candidate/dashboard', label: 'Dashboard', icon: LayoutDashboard, mobile: true },
    { to: '/candidate/jobs', label: 'Find jobs', icon: Search, mobile: true },
    { to: '/candidate/applications', label: 'Applications', icon: Send, mobile: true },
    { to: '/candidate/interviews', label: 'Interviews & offers', icon: CalendarCheck },
    { to: '/candidate/saved-jobs', label: 'Saved jobs', icon: Bookmark },
    { to: '/candidate/invites', label: 'Invites', icon: MailPlus },
    { to: '/candidate/profile', label: 'My profile', icon: UserCircle, mobile: true },
    { to: '/candidate/resume', label: 'Resumes', icon: FileText },
    { to: '/candidate/insights', label: 'My insights', icon: BarChart3 },
    { to: '/candidate/verification', label: 'Verification', icon: ShieldCheck },
    { to: '/candidate/assessments', label: 'Skill tests', icon: ClipboardCheck },
    { to: '/candidate/payments', label: 'Payments', icon: CreditCard },
    { to: '/candidate/referrals', label: 'Refer & earn', icon: Gift },
    { to: '/candidate/support', label: 'Help & support', icon: LifeBuoy },
    { to: '/candidate/notifications', label: 'Notifications', icon: Bell },
    { to: '/candidate/settings', label: 'Settings', icon: Settings },
  ],
};

export const EMPLOYER_NAV: NavConfig = {
  title: 'Employer',
  theme: 'light',
  items: [
    { to: '/employer/dashboard', label: 'Dashboard', icon: LayoutDashboard, mobile: true },
    { to: '/employer/jobs', label: 'Jobs', icon: Briefcase, permission: 'job.create', mobile: true },
    { to: '/employer/applications', label: 'Applications', icon: Inbox, permission: 'application.view', mobile: true },
    { to: '/employer/interviews', label: 'Interviews', icon: CalendarCheck, permission: 'interview.manage' },
    { to: '/employer/hires', label: 'Hires', icon: Handshake, permission: 'offer.manage' },
    { to: '/employer/insights', label: 'Hiring insights', icon: BarChart3, permission: 'application.view' },
    { to: '/employer/talent', label: 'Resume database', icon: UserSearch, permission: 'candidate.view' },
    { to: '/employer/shortlists', label: 'Shortlists', icon: Bookmark, permission: 'candidate.view' },
    {
      to: '/employer/requirements',
      label: 'Hire with TalentBabu',
      icon: ClipboardList,
      permission: 'requirement.view',
    },
    { to: '/employer/company', label: 'Company profile', icon: Building2, mobile: true },
    { to: '/employer/billing', label: 'Plans & billing', icon: CreditCard, permission: 'subscription.view' },
    { to: '/employer/support', label: 'Help & support', icon: LifeBuoy },
    { to: '/employer/notifications', label: 'Notifications', icon: Bell },
    { to: '/employer/settings', label: 'Settings', icon: Settings },
  ],
};

export const RECRUITER_NAV: NavConfig = {
  title: 'Recruiter',
  theme: 'light',
  items: [
    { to: '/recruiter/dashboard', label: 'Dashboard', icon: LayoutDashboard, mobile: true },
    {
      to: '/recruiter/requirements',
      label: 'Requirements',
      icon: ClipboardList,
      permission: 'requirement.view',
      mobile: true,
    },
    { to: '/recruiter/tasks', label: 'Tasks', icon: ListTodo, permission: 'task.view', mobile: true },
    { to: '/recruiter/leads', label: 'Leads', icon: Target, permission: 'crm.view' },
    { to: '/recruiter/interviews', label: 'Interviews', icon: CalendarCheck, permission: 'interview.manage' },
    { to: '/recruiter/placements', label: 'Offers & placements', icon: Handshake, permission: 'offer.manage' },
    { to: '/recruiter/jobs', label: 'Jobs', icon: Briefcase, permission: 'job.create' },
    { to: '/recruiter/applications', label: 'Applications', icon: Inbox, permission: 'application.view', mobile: true },
    { to: '/recruiter/candidates', label: 'Candidates', icon: UsersRound, permission: 'candidate.view' },
    { to: '/recruiter/companies', label: 'Companies', icon: Building2, permission: 'company.view' },
    {
      to: '/recruiter/verification',
      label: 'Verification queue',
      icon: ShieldCheck,
      permission: 'verification.manage',
    },
    { to: '/recruiter/analytics', label: 'My performance', icon: TrendingUp },
    { to: '/recruiter/notifications', label: 'Notifications', icon: Bell },
    { to: '/recruiter/settings', label: 'Settings', icon: Settings },
  ],
};

export const ADMIN_NAV: NavConfig = {
  title: 'Admin',
  theme: 'dark',
  items: [
    { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard, mobile: true },
    { to: '/admin/analytics', label: 'Analytics', icon: BarChart3, permission: 'analytics.view' },
    { to: '/admin/reports', label: 'Reports', icon: FileSpreadsheet, permission: 'report.view' },
    { to: '/admin/jobs', label: 'Jobs', icon: Briefcase, permission: 'job.approve', mobile: true },
    { to: '/admin/applications', label: 'Applications', icon: Inbox, permission: 'application.view' },
    { to: '/admin/requirements', label: 'Requirements', icon: ClipboardList, permission: 'requirement.view' },
    { to: '/admin/leads', label: 'Leads', icon: Target, permission: 'crm.view' },
    { to: '/admin/interviews', label: 'Interviews', icon: CalendarCheck, permission: 'interview.manage' },
    { to: '/admin/placements', label: 'Placements', icon: Handshake, permission: 'placement.manage' },
    { to: '/admin/recruiters', label: 'Recruiter performance', icon: TrendingUp, permission: 'recruiter.view' },
    { to: '/admin/tasks', label: 'Team tasks', icon: ListTodo, permission: 'task.view' },
    { to: '/admin/candidates', label: 'Candidates', icon: UsersRound, permission: 'candidate.view' },
    { to: '/admin/companies', label: 'Companies', icon: Building2, permission: 'company.view', mobile: true },
    { to: '/admin/verification', label: 'Verification', icon: ShieldCheck, permission: 'verification.manage' },
    { to: '/admin/assessments', label: 'Assessments', icon: ClipboardCheck, permission: 'assessment.manage' },
    { to: '/admin/finance', label: 'Finance', icon: IndianRupee, permission: 'payment.view' },
    { to: '/admin/plans', label: 'Plans', icon: Layers, permission: 'plan.manage' },
    { to: '/admin/users', label: 'Staff & users', icon: Users, permission: 'user.manage' },
    { to: '/admin/settings', label: 'Business settings', icon: SlidersHorizontal, permission: 'settings.manage' },
    { to: '/admin/support', label: 'Support desk', icon: LifeBuoy, permission: 'support.view' },
    { to: '/admin/referrals', label: 'Referral rewards', icon: Gift, permission: 'payment.view' },
    { to: '/admin/content', label: 'Content', icon: LayoutTemplate, permission: 'content.manage' },
    { to: '/admin/blog', label: 'Blogs', icon: Newspaper, permission: 'blog.manage' },
    { to: '/admin/seo', label: 'SEO pages', icon: Globe, permission: 'seo.manage' },
    { to: '/admin/messaging', label: 'Messaging', icon: MessagesSquare, permission: 'notification.manage' },
    { to: '/admin/audit-logs', label: 'Audit logs', icon: ScrollText, permission: 'audit.view' },
    { to: '/admin/notifications', label: 'Notifications', icon: Bell },
    { to: '/admin/payment-logs', label: 'Payment logs', icon: ReceiptText, permission: 'content.manage' },
    { to: '/admin/account', label: 'My account', icon: UserCircle, mobile: true },
  ],
};
